import type { ChipFilterValue } from './chipFilters';
import type { CollectionFormat } from './collectionFormat';
import type { DealFormDataWithTargeting } from './deal';
import type { BrandFloorFormParams, FloorRuleApi } from './floorrule';
import type { ConstValues } from './helpers';
import type { OptionsMap } from './options';
import type { QueryVar } from './serverSideCriteria';
import { type AllTableCriteria, CriteriaDimension } from './tableCriteria';
import type { UrlTargeting } from './targeting/domains';
import type { Targeting, TargetingState } from './targeting/targeting';

export type PackageEstimateRange = {
  low: string;
  high: string;
};

export type PackageEstimates = {
  reach?: PackageEstimateRange | null;
  cpm?: PackageEstimateRange | null;
  market_requests?: PackageEstimateRange | null;
};

export interface Package {
  id: string;
  uid: string;
  account_id: string;
  account_uid: string;
  domains: string | null;
  logo: string | null;
  name: string;
  notes: string;
  type: string;
  type_full: string;
  urls: Record<string, boolean> | string[] | null;
  locked_internal: boolean;
  rate_card_cpm: string | null;
  discounted_cpm: string | null;
  private_market: PrivateMarket | null;
  targeting: Targeting;
  is_curated?: boolean;
  estimates?: PackageEstimates | null;
}

export type PackageListItem = {
  account_id: string;
  account_uid: string;
  discounted_cpm: string | null;
  id: string;
  name: string;
  rate_card_cpm: string | null;
  type: string;
  type_full: string;
  uid: string;
  ad_placement?: string | null;
  device_type?: string | null;
  distribution_channel?: string | null;
  locked_internal: boolean;
  targeting?: Targeting;
  is_curated?: boolean;
  estimates?: PackageEstimates | null;
};

export interface PrivateMarket {
  default_discounted_cpm: string | null;
  default_rate_card_cpm: string | null;
  discoverable: '0' | '1';
}

export interface TargetingWithUrlTargeting extends Targeting {
  url_targeting?: UrlTargeting | null;
}

export interface PackageWithTargeting extends Package {
  url_targeting?: UrlTargeting | null;
  targeting: Targeting;
}

export interface PackageFormState
  extends Omit<
    Package,
    'private_market' | 'discounted_cpm' | 'urls' | 'uid' | 'type' | 'type_full' | 'account_id' | 'id' | 'targeting'
  > {
  domains: string;
  logo: string;
  urls: string[];
  rate_card_cpm: string;
  private_market: {
    default_discounted_cpm: string;
    default_rate_card_cpm: string;
    discoverable: boolean;
  };
  targeting: TargetingState;
  url_targeting?: UrlTargeting | null;
  estimates?: EstimatesState;
}

export type PackageFormParams = {
  deal?: DealFormDataWithTargeting;
} & BrandFloorFormParams;

export const PackageStatus = {
  ACTIVE: 'Active',
  INACTIVE: 'Inactive',
} as const;
export type PackageStatus = ConstValues<typeof PackageStatus>;

export const PackageFields = {
  ID: 'id',
  ACCOUNT_ID: 'account_id',
  ACCOUNT_UID: 'account_uid',
  NAME: 'name',
  DESCRIPTION: 'notes',
  LOGO: 'logo',
  DOMAINS: 'domains',
  URLS: 'urls',
  RATE: 'rate_card_cpm',
  PRIVATE_MARKET: 'private_market',
  TARGETING: 'targeting',
  LOCKED_INTERNAL: 'locked_internal',
  IS_CURATED: 'is_curated',
  ESTIMATES: 'estimates',
} as const;
export type PackageFields = ConstValues<typeof PackageFields>;

export const EstimateRangeFields = {
  CPM: 'cpm',
  REACH: 'reach',
  MARKET_REQUESTS: 'market_requests',
} as const;
export type EstimateRangeFields = ConstValues<typeof EstimateRangeFields>;

export const EstimateRangeFieldTypes = {
  CPM_LOW: `${PackageFields.ESTIMATES}.${EstimateRangeFields.CPM}.low`,
  CPM_HIGH: `${PackageFields.ESTIMATES}.${EstimateRangeFields.CPM}.high`,
  REACH_LOW: `${PackageFields.ESTIMATES}.${EstimateRangeFields.REACH}.low`,
  REACH_HIGH: `${PackageFields.ESTIMATES}.${EstimateRangeFields.REACH}.high`,
  MARKET_REQUESTS_LOW: `${PackageFields.ESTIMATES}.${EstimateRangeFields.MARKET_REQUESTS}.low`,
  MARKET_REQUESTS_HIGH: `${PackageFields.ESTIMATES}.${EstimateRangeFields.MARKET_REQUESTS}.high`,
} as const;
export type EstimateRangeFieldTypes = ConstValues<typeof EstimateRangeFieldTypes>;

export const PrivateMarketFields = {
  DEFAULT_RATE: 'default_rate_card_cpm',
  DISCOUNTED_RATE: 'default_discounted_cpm',
  DISCOVERABLE: 'discoverable',
} as const;
export type PrivateMarketFields = ConstValues<typeof PrivateMarketFields>;

export interface EstimatesState {
  [EstimateRangeFields.CPM]: PackageEstimateRange;
  [EstimateRangeFields.REACH]: PackageEstimateRange;
  [EstimateRangeFields.MARKET_REQUESTS]: PackageEstimateRange;
}

type EditModePayload = Partial<PackageListItem> | null;
export type PackageCloneData = Pick<PackageListItem, 'id' | 'uid' | 'name'>;

export type ClonePackagePayload = {
  packages: PackageCloneData[];
  editModePayload?: EditModePayload;
};

export const CuratedPackageAction = {
  DELETE: 'DELETE',
  SHOW_LIST_TAB: 'SHOW_LIST_TAB',
  UPDATE: 'UPDATE',
} as const;

export type CuratedPackageActionType = ConstValues<typeof CuratedPackageAction>;

export type DefaultPackageFloorsResponse = { has_more: boolean; objects: never[] };

export interface FetchPackageFloorsProps {
  limit?: number;
  fields?: string;
  status?: string;
  offset?: number;
  sort?: string;
}

export type FetchPackageFloorsResponse = CollectionFormat<FloorRuleApi>;

export type DeviceTypeOptionsFetchResponse = {
  isLoading: boolean;
  deviceTypeOptions: OptionsMap;
  // biome-ignore lint/suspicious/noExplicitAny: any is needed here
  refetch?: (props?: any) => any;
};

type ByPhraseFilter = {
  fields: string[];
  phrase: string;
};

export type PackageFilterCriteriaOA = AllTableCriteria<PackagesListFiltersValue> & {
  [CriteriaDimension.FILTERS]: {
    byPhrase?: ByPhraseFilter;
  };
};

export type CriteriaObjectOa = {
  criteriaGql: QueryVar;
  criteria: PackageFilterCriteriaOA;
  accountNetworkId?: string;
  onPageNumberCorrection?: (pageNumber: number) => void;
};

export type PackagesListFiltersValue = {
  phrase: string;
  account_uid?: string[];
  rate_card_cpm?: ChipFilterValue;
  discounted_cpm?: ChipFilterValue;
  ad_placement?: ChipFilterValue;
  device_type?: ChipFilterValue;
  distribution_channel?: ChipFilterValue;
  is_curated?: boolean;
};
